// Phase 17 Plan 01 — feature-scoped group cancel hook (CXL-01/02/03).
//
// XC-11: zero UserWrapperContext state — closure over GroupedBooking + derived
// disclosure only. The modal (Plan 17-02) passes group + viewerMemberId down as
// props; this hook handles the mutation + invalidation lifecycle.
//
// Threat mitigations:
//   T-17-03 — refund disclosure derived from backend total_cents + start_time only
//              via deriveGroupRefund; never client-recomputed from Stripe fields.
//   T-17-04 — isPending guards the UI button (Plan 17-02); no optimistic update.

import { useToast } from '@chakra-ui/react'
import { useMutation, useQueryClient, QueryClient } from 'react-query'
import { deleteJoin } from '../../api/user/notifications/Notifications'
import { deriveGroupRefund, buildAffectedNames } from './groupCancel'
import type { GroupedBooking, GroupCancelDisclosure } from './groupCancel'

const formatUsd = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

export interface UseGroupCancelReturn {
  disclosure: GroupCancelDisclosure | null
  names: string
  isPending: boolean
  isError: boolean
  confirmCancel: () => void
}

export const useGroupCancel = ({
  group,
  viewerMemberId,
  onClose,
  queryClient: injectedClient,
}: {
  group: GroupedBooking | null
  viewerMemberId: string | null
  onClose: () => void
  queryClient?: QueryClient
}): UseGroupCancelReturn => {
  const toast = useToast()
  // XC-11 test-injection pattern (mirrors useGroupCheckout): accept an optional
  // injected queryClient so unit tests can pass a spy while the provider client
  // is the same instance. In production callers omit this prop.
  // CR-01: useQueryClient() must be called unconditionally (Rules of Hooks).
  const providerClient = useQueryClient()
  const contextClient = injectedClient ?? providerClient

  const disclosure = group ? deriveGroupRefund(group) : null
  const names = group ? buildAffectedNames(group.rows, viewerMemberId) : ''

  const cancelMutation = useMutation(['deleteJoin'], () => deleteJoin({ id: group!.eventId }), {
    onSuccess: () => {
      toast({
        title: disclosure?.refundable && disclosure.refundCents > 0
          ? `Booking cancelled — ${formatUsd(disclosure.refundCents)} refund on its way`
          : 'Booking cancelled',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      // CXL-03: dual array-keyed invalidation — closes the single-key gap in
      // UpcomingEvents.tsx (which only invalidates ['upcomingEvents']).
      contextClient.invalidateQueries(['upcomingEvents'])
      contextClient.invalidateQueries(['getVenueEvents'])
      onClose()
    },
    onError: () => {
      toast({
        title: 'Cancel failed — please try again or contact support',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      // No invalidation or onClose on error (T-17-04 / CXL-03).
    },
  })

  return {
    disclosure,
    names,
    isPending: cancelMutation.isLoading,
    isError: cancelMutation.isError,
    confirmCancel: () => cancelMutation.mutate(),
  }
}

export default useGroupCancel
